/* eslint-disable */
class MegaMenuOpener extends HTMLElement {
  constructor() {
    super();

    this.closeTimeout = null;
    this.openTimeout = null;
    this.lastMousePosition = null;
    this.velocity = 0;

    // track last interaction to avoid hover fighting keyboard
    this.lastInput = "mouse"; // "mouse" | "keyboard"

    this.toggleBtn =
      this.querySelector("[data-mega-menu-toggle]") ||
      this.querySelector("[data-mega-toggle]");
    this.topLink =
      this.querySelector("[data-mega-menu-link]") || this.querySelector("a");
    this.content = this.querySelector("[data-mega-menu-content]");

    if (!this.toggleBtn || !this.content) return;

    // ---- helpers ----
    this._isOpen = () => this.toggleBtn.getAttribute("aria-expanded") === "true";

    this._baseLabel = () => {
      const raw =
        this.toggleBtn.getAttribute("aria-label") ||
        this.topLink?.textContent?.trim() ||
        "menu";
      return String(raw)
        .replace(/^(Open|Close)\s+/i, "")
        .replace(/\bmenu\b$/i, "")
        .trim() || "menu";
    };

    this._setExpanded = (isOpen) => {
      this.toggleBtn.setAttribute("aria-expanded", isOpen ? "true" : "false");
      this.toggleBtn.setAttribute(
        "aria-label",
        `${isOpen ? "Close" : "Open"} ${this._baseLabel()} menu`,
      );

      // Panel accessibility + tabbability control
      this.content.hidden = !isOpen;                 // removes from tab order in all browsers
      this.content.setAttribute("aria-hidden", (!isOpen).toString());

      if (isOpen) {
        this.content.removeAttribute("inert");
      } else {
        this.content.setAttribute("inert", "");
      }
      this.classList.toggle("open", isOpen);
    };

    this._closeOtherOpenMenus = () => {
      document.querySelectorAll("mega-menu-opener.open").forEach((el) => {
        if (el === this) return;
        el._close?.(true);
      });
    };

    this._focusFirstItem = () => {
      const first = this.content.querySelector(
        "a,button,input,select,textarea,[tabindex]:not([tabindex='-1'])",
      );
      first?.focus?.();
    };

    // ---- input tracking ----
    this._onAnyKeyDown = (e) => {
      // if user is navigating with keyboard, don't let hover auto-open
      if (e.key === "Tab" || e.key === "Enter" || e.key === " " || e.key === "Escape") {
        this.lastInput = "keyboard";
      }

      // Esc closes
      if (e.key !== "Escape") return;
      if (!this._isOpen()) return;

      this._close(true);
      (this.toggleBtn || this.topLink)?.focus?.();
    };
    document.addEventListener("keydown", this._onAnyKeyDown);

    this._onPointerMove = () => {
      this.lastInput = "mouse";
    };
    window.addEventListener("pointermove", this._onPointerMove, { passive: true });

    // ---- mouse velocity (optional delay behavior) ----
    this._onMouseMove = (e) => {
      const next = { y: e.pageY, time: Date.now() };
      if (this.lastMousePosition) {
        const dy = next.y - this.lastMousePosition.y;
        const dt = next.time - this.lastMousePosition.time || 1;
        this.velocity = dy / dt;
      }
      this.lastMousePosition = next;
    };
    document.addEventListener("mousemove", this._onMouseMove, { passive: true });

    // ---- hover open/close ----
this._onMouseEnter = () => {
  if (this.lastInput === "keyboard") return;

  clearTimeout(this.closeTimeout);
  clearTimeout(this.openTimeout);

  if (this._isOpen()) return;

  this._closeOtherOpenMenus();
  this._open(true);
};

this._onMouseLeave = (e) => {
  if (this.lastInput === "keyboard") return;

  // if moving to something still inside this mega-menu-opener (like the submenu), don't close
  if (e && this.contains(e.relatedTarget)) return;

  clearTimeout(this.openTimeout);

  this.closeTimeout = window.setTimeout(() => {
    this._close(true);
  }, 150);
};

this.addEventListener("mouseenter", this._onMouseEnter);
this.addEventListener("mouseleave", this._onMouseLeave);

    // ---- button click (works for mouse + Enter/Space) ----
    this._onToggleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();

      // keyboard activated click has e.detail === 0 in most browsers
      if (e.detail === 0) this.lastInput = "keyboard";

      if (this._isOpen()) {
        this._close(true);
        return;
      }

      this._closeOtherOpenMenus();
      this._open(true);
      this._focusFirstItem();
    };
    this.toggleBtn.addEventListener("click", this._onToggleClick);

    // ---- close when focus leaves the whole component ----
    this._onFocusOut = (e) => {
      if (this.contains(e.relatedTarget)) return;
      this._close(false);
    };
    this.addEventListener("focusout", this._onFocusOut);
  }

  disconnectedCallback() {
    document.removeEventListener("mousemove", this._onMouseMove);
    document.removeEventListener("keydown", this._onAnyKeyDown);
    window.removeEventListener("pointermove", this._onPointerMove);

    this.removeEventListener("mouseenter", this._onMouseEnter);
    this.removeEventListener("mouseleave", this._onMouseLeave);
    this.removeEventListener("focusout", this._onFocusOut);

    this.toggleBtn?.removeEventListener("click", this._onToggleClick);
  }

  _open(force = false) {
    clearTimeout(this.closeTimeout);
    clearTimeout(this.openTimeout);

    if (this._isOpen()) {
      this._setExpanded(true);
      return;
    }

    if (force) {
      this._setExpanded(true);
      this.dispatchEvent(new CustomEvent("menu:open", { bubbles: true }));
      return;
    }

    this.openTimeout = window.setTimeout(() => {
      this._setExpanded(true);
      this.dispatchEvent(new CustomEvent("menu:open", { bubbles: true }));
    }, 0);
  }

  _close(force = false) {
    clearTimeout(this.closeTimeout);
    clearTimeout(this.openTimeout);

    if (!this._isOpen()) {
      this._setExpanded(false);
      return;
    }

    const finish = () => {
      this._setExpanded(false);
      this.dispatchEvent(new CustomEvent("menu:close", { bubbles: true }));
    };

    if (force) return finish();
    this.closeTimeout = window.setTimeout(finish, 250);
  }
}

if (!customElements.get("mega-menu-opener")) {
  customElements.define("mega-menu-opener", MegaMenuOpener);
}